import { useEffect, useState } from 'react';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import clsx from 'clsx';

interface Props {
  anchor: Date;
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  onPick: (d: Date) => void;
}

export default function MiniMonth({ anchor, weekStartsOn = 0, onPick }: Props) {
  const [cursor, setCursor] = useState<Date>(() => startOfMonth(anchor));

  useEffect(() => {
    if (!isSameMonth(cursor, anchor)) setCursor(startOfMonth(anchor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor]);

  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(cursor), { weekStartsOn }),
    end: endOfWeek(endOfMonth(cursor), { weekStartsOn })
  });
  const today = new Date();
  const dows = Array.from({ length: 7 }, (_, i) =>
    format(addDays(startOfWeek(new Date(), { weekStartsOn }), i), 'EEEEEE')
  );

  return (
    <div className="border-b border-[var(--border)] p-2">
      <div className="flex items-center justify-between px-1 pb-2">
        <span className="text-sm font-semibold">{format(cursor, 'MMMM yyyy')}</span>
        <div className="flex">
          <button
            aria-label="previous month"
            onClick={() => setCursor((c) => addMonths(c, -1))}
            className="h-6 w-6 rounded text-[var(--text-muted)] hover:bg-[var(--bg-hover)]"
          >
            ‹
          </button>
          <button
            aria-label="next month"
            onClick={() => setCursor((c) => addMonths(c, 1))}
            className="h-6 w-6 rounded text-[var(--text-muted)] hover:bg-[var(--bg-hover)]"
          >
            ›
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px">
        {dows.map((d) => (
          <span key={d} className="py-1 text-center text-[0.7rem] text-[var(--text-faint)]">
            {d}
          </span>
        ))}
        {days.map((day) => {
          const other = !isSameMonth(day, cursor);
          const isToday = isSameDay(day, today);
          const active = isSameDay(day, anchor);
          return (
            <button
              key={day.toISOString()}
              onClick={() => onPick(day)}
              className={clsx(
                'mx-auto grid h-[26px] w-[26px] place-items-center rounded-full text-[0.78rem]',
                other && 'text-[var(--text-faint)]',
                isToday && !active && 'bg-[var(--today-bg)] font-semibold text-[var(--today-fg)]',
                active && 'bg-[var(--accent)] text-[var(--accent-fg)]',
                !isToday && !active && 'hover:bg-[var(--bg-hover)]'
              )}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
}
